import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Check, ChevronDown, Download, FileJson, FileSpreadsheet } from 'lucide-react'
import type { TemplateFormat } from '../../../types/infrastructure'

interface TriggerProps {
  showDropdown: boolean
  downloading: boolean
  disabled: boolean
  onToggle: () => void
  t: TFunction<'cases'>
}

export function ExportTrigger({
  showDropdown,
  downloading,
  disabled,
  onToggle,
  t,
}: TriggerProps): ReactElement {
  return (
    <button
      onClick={onToggle}
      disabled={disabled || downloading}
      className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
    >
      <Download className={`h-4 w-4 ${downloading ? 'animate-pulse' : ''}`} />
      {downloading ? t('infrastructure.downloading') : t('infrastructure.exportTemplate')}
      <ChevronDown className={`h-4 w-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
    </button>
  )
}

interface ToggleProps {
  includeExamples: boolean
  onToggle: () => void
  t: TFunction<'cases'>
}

export function IncludeExamplesToggle({ includeExamples, onToggle, t }: ToggleProps): ReactElement {
  return (
    <div className="border-b border-slate-200 p-3 dark:border-slate-700">
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-2 text-sm text-slate-700 dark:text-slate-300"
      >
        <div
          className={`flex h-4 w-4 items-center justify-center rounded border transition-colors ${
            includeExamples
              ? 'border-fuchsia-600 bg-fuchsia-600'
              : 'border-slate-300 dark:border-slate-600'
          }`}
        >
          {includeExamples && <Check className="h-3 w-3 text-white" />}
        </div>
        {t('infrastructure.includeExampleRows')}
      </button>
      <p className="mt-1 ml-6 text-xs text-slate-500 dark:text-slate-400">
        {t('infrastructure.includeExampleRowsDescription')}
      </p>
    </div>
  )
}

interface OptionsProps {
  downloading: boolean
  onDownload: (format: TemplateFormat) => void
  t: TFunction<'cases'>
}

export function DownloadOptions({ downloading, onDownload, t }: OptionsProps): ReactElement {
  return (
    <div className="p-1">
      <button
        onClick={() => {
          onDownload('csv')
        }}
        disabled={downloading}
        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:text-slate-300 dark:hover:bg-slate-700"
      >
        <FileSpreadsheet className="h-4 w-4 text-green-600 dark:text-green-500" />
        {t('infrastructure.downloadCsvTemplate')}
      </button>
      <button
        onClick={() => {
          onDownload('json')
        }}
        disabled={downloading}
        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:text-slate-300 dark:hover:bg-slate-700"
      >
        <FileJson className="h-4 w-4 text-amber-600 dark:text-amber-500" />
        {t('infrastructure.downloadJsonTemplate')}
      </button>
    </div>
  )
}

interface ErrorProps {
  error: string | null
}

export function ExportErrorBanner({ error }: ErrorProps): ReactElement | null {
  if (!error) return null
  return (
    <div className="border-t border-slate-200 p-3 dark:border-slate-700">
      <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
    </div>
  )
}
